import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Search } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/Button';
import styles from './NotFoundPage.module.css';

export const NotFoundPage: React.FC = () => {
    const { language } = useLanguage();

    const content = {
        de: {
            title: '404',
            subtitle: 'Seite nicht gefunden',
            description: 'Die gesuchte Seite existiert leider nicht oder wurde verschoben.',
            homeButton: 'Zur Startseite',
            backButton: 'Zurück'
        },
        en: {
            title: '404',
            subtitle: 'Page Not Found',
            description: 'The page you\'re looking for doesn\'t exist or has been moved.',
            homeButton: 'Go Home',
            backButton: 'Go Back'
        }
    };

    const t = content[language];

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <motion.div
                    className={styles.content}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.15 }}
                >
                    <motion.div
                        className={styles.icon}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0, type: 'spring', stiffness: 200 }}
                    >
                        <Search size={48} />
                    </motion.div>

                    <motion.h1
                        className={styles.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0 }}
                    >
                        {t.title}
                    </motion.h1>

                    <motion.h2
                        className={styles.subtitle}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0 }}
                    >
                        {t.subtitle}
                    </motion.h2>

                    <motion.p
                        className={styles.description}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                    >
                        {t.description}
                    </motion.p>

                    <motion.div
                        className={styles.actions}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                    >
                        <Link to="/">
                            <Button variant="primary" size="lg" icon={<Home size={20} />}>
                                {t.homeButton}
                            </Button>
                        </Link>
                        <Button
                            variant="secondary"
                            size="lg"
                            icon={<ArrowLeft size={20} />}
                            onClick={() => window.history.back()}
                        >
                            {t.backButton}
                        </Button>
                    </motion.div>
                </motion.div>
            </div>

            {/* Background decoration */}
            <div className={styles.background}>
                <div className={styles.glow1} />
                <div className={styles.glow2} />
            </div>
        </div>
    );
};

export default NotFoundPage;
